import { useCallback, useEffect, useState } from "react";

import { SWReactIcons } from "@similarweb/icons";
import Sentry from "@similarweb/sentry-logger";
import { colorsPalettes } from "@similarweb/styles";
import { Stack, Text } from "@similarweb/ui-components/foundations";

import { swSettings } from "app/common/services/swSettings";
import { DEFAULT_HOMEPAGE_PREFERENCE_KEY, DEFAULT_HOMEPAGES } from "app/components/modals/constants";
import { translate } from "app/filters/i18nFilter";
import DataCreditsIndicatorContainer from "app/pages/sales-intelligence/modules/quota/containers/DataCreditsIndicatorContainer/DataCreditsIndicatorContainer";
import { useLastActivityQuery } from "app/pages/sales-intelligence/query/queries/useLastActivityQuery";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import { PreferencesService } from "app/services/preferences/preferencesService";

import { formatLastActivity } from "../../utils/formatLastActivity";
import { isDhlUser } from "../../utils/isDhlUser";

import {
    LoadSpinner,
    StyledColumn,
    StyledHomePageIconButton,
    StyledHomePageWrapper,
    StyledWelcomeTitleText,
} from "./styles";

type Props = {
    defaultHomepage: string;
    hasPageBeenVisited: boolean;
};

const HomePageTitle = ({ defaultHomepage, hasPageBeenVisited }: Props) => {
    const [isSetDefaultBtnVisible, setIsSetDefaultBtnVisible] = useState(false);
    const [isHomePageLoading, setHomePageLoading] = useState(false);
    const [isSelectedAsDefault, setIsSelectedAsDefault] = useState(false);

    const { data: lastActivity } = useLastActivityQuery();
    const showLastActivity = isDhlUser(swSettings);
    const lastActivityText = showLastActivity && lastActivity ? formatLastActivity(lastActivity) : "";

    const handleSetDefaultHome = useCallback(async () => {
        setHomePageLoading(true);

        try {
            await PreferencesService.add({
                [DEFAULT_HOMEPAGE_PREFERENCE_KEY]: DEFAULT_HOMEPAGES.SI,
            });

            setIsSelectedAsDefault(true);
            setHomePageLoading(false);
        } catch (error) {
            Sentry.error("Preferences service: error adding preference", error);
        }
    }, []);

    useEffect(() => {
        const isVisible = defaultHomepage !== DEFAULT_HOMEPAGES.SI || isSelectedAsDefault;
        const hasOtherSolutions = salesSettingsHelper.isMoreThenOneSolutionWithSi();

        setIsSetDefaultBtnVisible(isVisible && hasOtherSolutions);
    }, [defaultHomepage, isSelectedAsDefault]);

    return (
        <StyledColumn>
            <StyledHomePageWrapper>
                <Stack gap="4px">
                    <StyledWelcomeTitleText>
                        {translate(
                            hasPageBeenVisited ? "si.pages.home.welcome-back.text" : "si.pages.home.welcome.text",
                            {
                                firstName: swSettings.user.firstname,
                            },
                        )}
                        &nbsp;👋🏻
                    </StyledWelcomeTitleText>
                    {lastActivityText ? (
                        <Text
                            fontVariant="body2"
                            data-automation="home-page-last-activity"
                            color={colorsPalettes.carbon[400]}
                        >
                            {translate("si.pages.home.last_activity.text")} {lastActivityText}
                        </Text>
                    ) : null}
                </Stack>
                <DataCreditsIndicatorContainer />

                {isSetDefaultBtnVisible ? (
                    <StyledHomePageIconButton
                        disabled={isSelectedAsDefault}
                        type="flat"
                        onClick={handleSetDefaultHome}
                    >
                        {isHomePageLoading ? (
                            <LoadSpinner />
                        ) : (
                            <SWReactIcons
                                iconName={isSelectedAsDefault ? "checked" : "homepage"}
                                size="xs"
                            />
                        )}

                        {translate("si.pages.home.set_home_btn.text")}
                    </StyledHomePageIconButton>
                ) : null}
            </StyledHomePageWrapper>
        </StyledColumn>
    );
};

export default HomePageTitle;
